<template>
  <!-- prettier-ignore -->
  <portal
    v-if="value"
    :to="target">
    <transition
      name="fade"
      appear>
      <div
        :id="id"
        ref="rootElement"
        class="ovp-modal-container"
        @keydown.esc="handleEsc">
        <div
          tabIndex="-1"
          class="overlay"
          @click="handleOverlayClick"
        />
        <transition
          name="scale"
          appear>
          <div
            :role="role"
            :class="dialogClasses">
            <div
              role="document"
              :class="[{ 'document--inverse': inverse }, 'document']">
              <button
                v-if="closeOptions.closeIcon"
                data-a11y-dialog-hide
                type="button"
                aria-label="Close Dialog"
                class="close-button"
                @click="close">
                <ovp-icon
                  art="light"
                  name="xmark"
                />
              </button>
              <slot :modal-props="slotProps" />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </portal>
</template>

<script>
import A11yDialog from 'a11y-dialog'

import { LogHelper } from '@/helpers'
import { EVENT_CLOSE_ALL_MODALS } from '@/config/events'

/**
 * Represent the different types a dialog can be closed:
 *  * LOCKED:   Dialog has no close option, close option must be defined within parent component
 *  * X_ONLY:   Dialog can be closed via Close Icon or ESC-Key
 *  * OPEN:     Dialog can be closed via Close Icon, ESC-Key and by clicking outside of the dialog
 *  * DEFAULT:  Dialog can be closed via Close Icon, ESC-Key and by clicking outside of the dialog
 *
 * @type {Readonly<{LOCKED: {closeOnMaskClick: boolean, closeOnEsc: boolean, closeIcon: boolean}, X_ONLY: {closeOnMaskClick: boolean, closeOnEsc: boolean, closeIcon: boolean}, DEFAULT: {closeOnMaskClick: boolean, closeOnEsc: boolean, closeIcon: boolean}, OPEN: {closeOnMaskClick: boolean, closeOnEsc: boolean, closeIcon: boolean}}>}
 */
const DialogCloseTypes = Object.freeze({
  LOCKED: {
    closeIcon: false,
    closeOnMaskClick: false,
    closeOnEsc: false,
  },
  X_ONLY: {
    closeIcon: true,
    closeOnMaskClick: false,
    closeOnEsc: true,
  },
  OPEN: {
    closeIcon: true,
    closeOnMaskClick: true,
    closeOnEsc: true,
  },
  DEFAULT: {
    closeIcon: true,
    closeOnMaskClick: true,
    closeOnEsc: true,
  },
})

export default {
  name: 'OvpModal',

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    xOnly: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: 'modals',
    },
    inverse: {
      type: Boolean,
      default: false,
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
    backgroundBlur: {
      type: String,
      default: 'div.ovp-container',
    },
    themes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      id: null,
      dialog: null,
      slotProps: {
        // TODO: Remove submit & cancel, as only needed once
        close: this.close,
        submit: this.submit,
        cancel: this.cancel,
      },
    }
  },

  created() {
    // Give each dialog a unique dialog-Id
    this.id = `ovp-dialog-${this._uid}`

    this.$EventBus.$on(EVENT_CLOSE_ALL_MODALS, this.close)
  },

  beforeDestroy() {
    // Needed if modal closed by tab sync
    if (!this.hasSiblings()) {
      this.reset()
    }
    this.$EventBus.$off(EVENT_CLOSE_ALL_MODALS, this.close)
  },

  watch: {
    value: {
      immediate: true,
      handler: function (val) {
        if (val) this.init()
        else this.destroy()
      },
    },

    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },

  computed: {
    /**
     * Retrieves the corresponding {@link DialogCloseTypes} depending on the type props.
     * If the there's no result for the given type prop, it returns the default DialogCloseType
     * @returns {*|{closeOnMaskClick: boolean, closeOnEsc: boolean, closeIcon: boolean}}
     */
    closeOptions() {
      if (this.locked) return DialogCloseTypes['LOCKED']
      if (this.xOnly) return DialogCloseTypes['X_ONLY']
      if (this.open) return DialogCloseTypes['OPEN']
      return DialogCloseTypes['DEFAULT']
    },

    dialogClasses() {
      const baseClasses = {
        dialog: true,
        'full-screen': this.fullScreen,
      }

      return [baseClasses, ...this.themes]
    },

    /**
     * Is used for defining which role-attribute is set on the dialog element
     * If a click outside should close the dialog, then the default 'dialog' is returned
     * Otherwise the 'alertdialog' string is returned, indicating that the dialog is not closeable on an outside click
     * @returns {string}
     */
    role() {
      return this.closeOptions.closeOnMaskClick ? 'dialog' : 'alertdialog'
    },
  },

  methods: {
    /**
     * Instantiates a new {@link A11yDialog} object
     */
    init() {
      this.$nextTick(
        function () {
          this.dialog = new A11yDialog(this.$refs.rootElement, '.ovp-configurator')
          this.addInternalEventListener(this.dialog)
          this.dialog.show() // needed for listener
        }.bind(this)
      )
    },

    /**
     * Destroys the current {@link A11yDialog} object if not undefined
     * Takes internally care of hiding the dialog and removing all associated listeners
     */
    destroy() {
      if (this.dialog) {
        this.close()
        this.dialog.destroy()
      }
    },

    /**
     * Add custom listeners to the {@link A11yDialog} object:
     *  * onShow: Set overflow='hidden' to the body element to prevent background scrolling only if there's not
     *            already another dialog open
     *  * onHide: Add a closing transition for the dialog
     * @param {A11yDialog} dialog
     */
    addInternalEventListener(dialog) {
      dialog.on('show', () => {
        // set fixed position on body (scrollY is 0 after setting position)
        const scrollY = window.scrollY
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollY}px`
        document.body.style.left = '0'
        document.body.style.right = '0'
        const blurEl = this.blurElement()
        blurEl?.style.setProperty('filter', 'blur(5px)')
        window.OVP_APP_DISABLE_NAVIGATION = true
      })
      dialog.on('hide', dialogEl => {
        // This is a workaround as the nested transitions leave events are not triggered due to the parent transition
        // See [here]{@link https://forum.vuejs.org/t/nested-transitions-not-engaging-javascript-leave-hooks/7207/3}
        // for detailed information
        const dialog = dialogEl.getElementsByClassName('dialog')[0]
        const styles = {
          '-webkit-transform': 'scale(1.1)',
          transform: 'scale(1.1)',
          transition: 'all .3s ease',
        }
        Object.assign(dialog.style, styles)
      })
    },

    /**
     * Closes a dialog by emitting the v-model input value (set to false) and a 'close' event.
     * If the dialog has no siblings it removes the overflow property for the body element to allow background
     * scrolling again.
     */
    close() {
      this.$emit('close')
      this.$emit('input', false)
      if (!this.hasSiblings()) {
        this.reset()
      }
    },

    /**
     * Emits a 'cancel' event to the parent and closes the dialog
     */
    cancel() {
      this.$emit('cancel')
      this.close()
    },

    /**
     * Reset blocking of navigation, scrolling, remove blur
     */
    reset() {
      if (document.body.style.position === 'fixed') {
        const scrollY = document.body.style.top

        document.body.style.removeProperty('position')
        document.body.style.removeProperty('top')
        document.body.style.removeProperty('left')
        document.body.style.removeProperty('right')

        window.scrollTo(0, Math.abs(parseInt(scrollY || '0', 10)))
      }

      const blurEl = this.blurElement()
      blurEl?.style.removeProperty('filter')
      window.OVP_APP_DISABLE_NAVIGATION = false
    },

    /**
     * Emits a 'submit' event to the parent and closes the dialog
     */
    submit() {
      this.$emit('submit')
      this.close()
    },

    /**
     * Gets triggered by a keydown.esc event on an open dialog.
     * If the {@link closeOptions} of the dialog allow it, the dialog is closed upon the keydown.esc event.
     * Otherwise this behavior is prevented.
     * @param event
     */
    handleEsc(event) {
      if (!this.closeOptions.closeOnEsc) {
        event.stopPropagation()
        event.preventDefault()
      } else {
        this.close()
      }
    },

    /**
     * Gets triggered by the mouse click event on the overlay (outside of an open dialog).
     * If the {@link closeOptions} of the dialog allow it, the dialog is closed upon the mouse click event.
     * Otherwise this behavior is prevented.
     * @param event
     */
    handleOverlayClick(event) {
      if (!this.closeOptions.closeOnMaskClick) {
        event.stopPropagation()
        event.preventDefault()
      } else {
        this.close()
      }
    },

    /**
     * Check if there is already an existing dialog opened and sets the hasSiblings value accordingly.
     */
    hasSiblings() {
      const siblingsCount = this.$refs.rootElement?.parentElement.children.length
      return siblingsCount ? siblingsCount > 1 : false
    },

    /**
     * Gets the HTML element depending on the given backgroundBlur prop.
     * Returns null if no element is found with given selector.
     */
    blurElement() {
      return document.querySelector(this.backgroundBlur)
    },

    /**
     * Custom prop validation to throw an error if more than one type prop (locked, x_only, open) is set
     */
    validateProps() {
      const hasMultipleTypeProps = [this.locked, this.xOnly, this.open].filter(type => type === true).length > 1
      if (hasMultipleTypeProps) {
        LogHelper.error('Only one type prop can be set')
      }
    },
  },
}
</script>

<style scoped>
.ovp-modal-container,
.overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.ovp-modal-container {
  z-index: 9998;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay {
  background-color: rgba(25, 25, 25, 0.5);
}

.dialog {
  z-index: 9998;
  position: relative;
  background-color: #fff;
  height: 100%;
  width: 100%;
  overflow: auto;
}

.mobile--dark {
  background-color: var(--c-warm-gray);

  &.full-screen .close-button {
    background-color: inherit;
    top: 12px;
    right: 4px;
  }
}

.desktop--narrow {
  .close-button {
    left: calc((100% - ((100% - 960px) / 2)) - 42px);
    right: 0;
  }
}

@media (--md) {
  .dialog:not(.full-screen) {
    width: 90%;
    height: auto;
    max-height: 80vh;
  }
}

@media (--lg) {
  .dialog:not(.full-screen) {
    width: 960px;
  }
}

.document {
  margin: 0 0 70px;
  ul {
    margin-block-end: 1em;
    margin-inline-start: 0;
    margin-inline-end: 0;
    padding-inline-start: 40px;
  }

  &--inverse {
    background-color: var(--c-warm-gray);
  }

  @media (--md) {
    margin-bottom: 40px;
  }
}

.full-screen .close-button {
  background-color: var(--c-white);
}

.close-button {
  position: absolute;
  top: 0;
  right: 0;
  background-color: inherit;
  border: none;
  cursor: default;
  z-index: 9999;

  height: 42px;
  width: 42px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    outline: none;
  }

  svg {
    height: 24px;
    width: 24px;
  }
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.scale-enter,
.scale-leave-to {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.scale-enter-active,
.scale-leave,
.scale-leave-to,
.scale-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
</style>

<style name="ios">
/* Please dont scope this overwrite area for screw ios, thanks! */

/* iPhone 4 ----------- */
@media only screen and (min-device-width: 320px) and (max-device-width: 480px) and (-webkit-min-device-pixel-ratio: 2) {
  .document {
    margin-bottom: 85px;
  }
}

/* iPhone 5 ----------- */
@media only screen and (min-device-width: 320px) and (max-device-height: 568px) and (-webkit-device-pixel-ratio: 2) {
  .document {
    margin-bottom: 120px;
  }
}

/* iPhone 6, 7, 8 ----------- */
@media only screen and (min-device-width: 375px) and (max-device-height: 667px) and (-webkit-device-pixel-ratio: 2) {
  .document {
    margin-bottom: 85px;
  }
}

/* iPhone 6+, 7+, 8+ ----------- */
@media only screen and (min-device-width: 414px) and (max-device-height: 736px) and (-webkit-device-pixel-ratio: 2) {
  .document {
    margin-bottom: 120px;
  }
}

/* iPhone X ----------- */
@media only screen and (min-device-width: 375px) and (max-device-height: 812px) and (-webkit-device-pixel-ratio: 3) {
  .document {
    margin-bottom: 120px;
  }
}

/* iPhone XS Max, XR ----------- */
@media only screen and (min-device-width: 414px) and (max-device-height: 896px) and (-webkit-device-pixel-ratio: 3) {
  .document {
    margin-bottom: 120px;
  }
}
</style>
